import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, KeyRound, LogOut, Shield, User } from 'lucide-react';
import { useAuth } from '@/lib/auth/useAuth';
import { cn } from '@/lib/utils/cn';
import keycloak from '@/lib/auth/keycloak';

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
}

const ROLE_LABELS: Record<string, string> = {
  AUTHMATECH_SUPER_ADMIN:    'Super Admin',
  AUTHMATECH_ADMIN:          'Admin',
  AUTHMATECH_OPERATOR:       'Operator',
  AUTHMATECH_FINANCE:        'Finance',
  AUTHMATECH_SUPPORT:        'Support',
  AUTHMATECH_VIEWER:         'Viewer',
  AUTHMATECH_CLIENT_ADMIN:   'Client Admin',
  AUTHMATECH_CLIENT_OPERATOR:'Client Operator',
  AUTHMATECH_CLIENT_FINANCE: 'Client Finance',
  AUTHMATECH_CLIENT_VIEWER:  'Client Viewer',
};

const ROLE_ORDER = [
  'AUTHMATECH_SUPER_ADMIN',
  'AUTHMATECH_ADMIN',
  'AUTHMATECH_OPERATOR',
  'AUTHMATECH_FINANCE',
  'AUTHMATECH_SUPPORT',
  'AUTHMATECH_VIEWER',
  'AUTHMATECH_CLIENT_ADMIN',
  'AUTHMATECH_CLIENT_OPERATOR',
  'AUTHMATECH_CLIENT_FINANCE',
  'AUTHMATECH_CLIENT_VIEWER',
];

function primaryRole(roles: string[]): string {
  const top = ROLE_ORDER.find((r) => roles.includes(r));
  return top ? (ROLE_LABELS[top] ?? top) : '';
}

function isClientUser(roles: string[]): boolean {
  return roles.some((r) => r.startsWith('AUTHMATECH_CLIENT_'));
}

export function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!user) return null;

  const role = primaryRole(user.roles);
  const displayName = user.name || user.preferredUsername || '';
  const userInitials = displayName ? initials(displayName) : '?';
  const isClient = isClientUser(user.roles);
  const accountRoute = isClient ? '/my-account' : '/settings/api-keys';

  function handleChangePassword() {
    setOpen(false);
    // Redirect to Keycloak account management page for secure password change
    const accountUrl = keycloak.createAccountUrl();
    window.open(accountUrl, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'group flex items-center gap-1.5 rounded-lg px-1.5 py-1 transition-all duration-150',
          'text-fg-muted hover:bg-surface-subtle hover:text-fg',
          open && 'bg-surface-subtle text-fg',
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="User menu"
      >
        {/* Avatar */}
        <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[12px] font-bold text-white ring-2 ring-transparent transition-all group-hover:ring-primary-300/50">
          {userInitials}
          {/* Online dot */}
          <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-surface bg-emerald-400" aria-hidden />
        </div>
        <ChevronDown
          className={cn(
            'hidden sm:block h-3.5 w-3.5 shrink-0 transition-transform duration-150',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-72 origin-top-right animate-scale-in rounded-2xl border border-border bg-surface shadow-card-lg dark:shadow-none dark:border-white/[0.08] dark:bg-[#0d1829]"
        >
          {/* Profile header */}
          <div className="px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[16px] font-bold text-white ring-2 ring-primary-400/30">
                {userInitials}
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-surface bg-emerald-400" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-fg">{displayName}</div>
                <div className="truncate text-[11px] text-fg-muted">{user.email}</div>
                {role && (
                  <span className="mt-1.5 inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-[10px] font-semibold tracking-wide text-primary-700 dark:bg-primary-500/15 dark:text-primary-300">
                    {role}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-border/60" />

          {/* Actions */}
          <div className="p-1.5">
            <Link
              to={accountRoute}
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-fg-muted transition-colors hover:bg-surface-subtle hover:text-fg"
              role="menuitem"
            >
              <User className="h-4 w-4 shrink-0" aria-hidden />
              <span>My Account</span>
            </Link>

            <button
              type="button"
              onClick={handleChangePassword}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-fg-muted transition-colors hover:bg-surface-subtle hover:text-fg"
              role="menuitem"
            >
              <KeyRound className="h-4 w-4 shrink-0" aria-hidden />
              <span>Change Password</span>
            </button>

            {!isClient && (
              <Link
                to="/security"
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-fg-muted transition-colors hover:bg-surface-subtle hover:text-fg"
                role="menuitem"
              >
                <Shield className="h-4 w-4 shrink-0" aria-hidden />
                <span>Security & Access</span>
              </Link>
            )}
          </div>

          <div className="border-t border-border/60" />

          <div className="p-1.5">
            {/* Keycloak identity notice */}
            <p className="px-3 py-1.5 text-[10px] text-fg-subtle">
              Identity managed by Authmatech Identity (Keycloak)
            </p>
            <button
              type="button"
              onClick={() => { setOpen(false); logout(); }}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-fg-muted transition-colors hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10 dark:hover:text-rose-400"
              role="menuitem"
            >
              <LogOut className="h-4 w-4 shrink-0" aria-hidden />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
